import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatReadingLayoutLabel,
  stripHtmlForPreview,
} from '@/api/selectors';
import { useReadingsList } from '@/hooks/api/useReadings';
import { formatHistoryDate, t } from '@/i18n';
import styles from './ReadingsHistory.module.css';

const PAGE_SIZE = 3;

function truncate(text: string, max = 120): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

function ratingIcon(rating: string | null | undefined): string | null {
  if (rating === 'good') return String.fromCodePoint(0x1f496);
  if (rating === 'neutral') return String.fromCodePoint(0x1f60c);
  if (rating === 'bad') return String.fromCodePoint(0x1f62b);
  return null;
}

export function ReadingsHistory() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useReadingsList({ limit: PAGE_SIZE });

  const pages = data?.pages ?? [];
  const items = pages[page]?.items ?? [];
  const canPrev = page > 0;
  const canNext = page + 1 < pages.length || hasNextPage;
  const pageLoading = isFetchingNextPage && page + 1 >= pages.length;
  const showPagination = canPrev || canNext;

  const goPrev = () => {
    if (canPrev) setPage((current) => current - 1);
  };

  const goNext = async () => {
    if (page + 1 < pages.length) {
      setPage((current) => current + 1);
      return;
    }
    if (hasNextPage && !isFetchingNextPage) {
      const result = await fetchNextPage();
      if (result.data && result.data.pages.length > page + 1) {
        setPage((current) => current + 1);
      }
    }
  };

  const panelBody = (() => {
    if (isLoading) {
      return <p className={styles.muted}>{t.common.loadingMore}</p>;
    }

    if (isError) {
      return <p className={styles.muted}>{t.profile.history.loadFailed}</p>;
    }

    if (pages.length === 0 || (pages.length === 1 && items.length === 0)) {
      return <p className={styles.muted}>{t.profile.history.empty}</p>;
    }

    return (
      <>
        <ul className={styles.list}>
          {items.map((item) => {
            const rating = ratingIcon(item.rating);
            const preview = truncate(stripHtmlForPreview(item.interpretation), 130);

            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={styles.card}
                  onClick={() => navigate(`/result?id=${item.id}`)}
                  aria-label={t.profile.history.openReading}
                >
                  <div className={styles.cardTop}>
                    <span className={styles.layout}>
                      {formatReadingLayoutLabel(item.layoutType, item.type)}
                    </span>
                    {rating ? (
                      <span className={styles.rating} aria-hidden>
                        {rating}
                      </span>
                    ) : null}
                  </div>
                  <div className={styles.date}>{formatHistoryDate(item.createdAt)}</div>
                  {item.userQuestion ? (
                    <p className={styles.question}>{truncate(item.userQuestion, 90)}</p>
                  ) : null}
                  {preview ? <p className={styles.preview}>{preview}</p> : null}
                  <span className={styles.arrow} aria-hidden>
                    →
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {showPagination ? (
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={!canPrev}
              onClick={goPrev}
              aria-label={t.profile.history.prev}
            >
              ←
            </button>
            <button
              type="button"
              className={styles.pageBtn}
              disabled={!canNext || pageLoading}
              onClick={() => void goNext()}
              aria-label={t.profile.history.next}
            >
              {pageLoading ? '…' : '→'}
            </button>
          </div>
        ) : null}
      </>
    );
  })();

  return (
    <section className={styles.panel}>
      <div className={styles.head}>{t.profile.history.title}</div>
      <div className={styles.body}>{panelBody}</div>
    </section>
  );
}
